// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Building the files you leave with.
 *
 * ONE ZIP, ALWAYS. Safari suppresses every programmatic download after the first in a single user
 * gesture, silently — so a tool that fires four `<a download>` clicks hands an iPhone user one file
 * and no error, and they reasonably conclude they have all four. `fflate` is already here, so every
 * run produces `charsight-run.zip` containing everything, and the individual downloads stay as a
 * desktop convenience rather than as the mechanism.
 */

import { zipSync } from 'fflate';
import { CATEGORY_META } from '../scan/catalogue';
import { TIER_SLUGS } from '../scan/types';
import type { Finding } from '../scan/types';
import {
  blockOf,
  charName,
  formatCodePoint,
  scriptDisplayName,
  scriptOf,
  UCD_VERSION,
} from '../data/unicode';
import type { FileResult, RunResult } from '../types';
import { findingsCsv, type FindingRow } from './csv';
import { buildReport } from './report';
import { visibleText } from './visible';
import { mechanismLabel } from '../scan/htmlscan';

const CONTEXT_RADIUS = 24;

/** A window of surrounding text, with every flagged code point already made visible. */
function contextFor(text: string, f: Finding, offsetToIndex: (byte: number) => number): string {
  const i = offsetToIndex(f.byteStart);
  const j = offsetToIndex(f.byteEnd);
  if (i < 0) return '';
  const slice = text.slice(Math.max(0, i - CONTEXT_RADIUS), Math.min(text.length, j + CONTEXT_RADIUS));
  return visibleText(slice, { chipAsciiWhitespace: true });
}

export function findingRows(run: RunResult, texts: Map<string, string>): FindingRow[] {
  const rows: FindingRow[] = [];

  for (const file of run.files) {
    const text = texts.get(file.name) ?? '';
    // Byte offset -> string index, built once per file. Cheap, and it keeps the context window
    // honest on files containing astral characters, where the two indices diverge.
    const index = new Map<number, number>();
    if (text) {
      let byte = 0;
      let idx = 0;
      for (const ch of text) {
        index.set(byte, idx);
        byte += new TextEncoder().encode(ch).length;
        idx += ch.length;
      }
      index.set(byte, idx);
    }
    const toIndex = (b: number): number => index.get(b) ?? -1;

    for (const f of file.findings) {
      const meta = CATEGORY_META[f.category];
      rows.push({
        file: file.name,
        line: String(f.line),
        column: String(f.column),
        byteOffset: String(f.byteStart),
        byteLength: String(f.byteEnd - f.byteStart),
        codePoint: f.cp >= 0 ? formatCodePoint(f.cp) : '',
        unicodeName: f.cp >= 0 ? (charName(f.cp) ?? '') : '',
        block: f.cp >= 0 ? blockOf(f.cp) : '',
        script: f.cp >= 0 ? scriptDisplayName(scriptOf(f.cp)) : '',
        category: meta.label,
        consequence: TIER_SLUGS[meta.tier],
        confidence: f.severity,
        reason: f.reason,
        decodedPayload: f.decoded ? visibleText(f.decoded, { chipAsciiWhitespace: true }) : '',
        action: f.action,
        replacement: f.replacement ? visibleText(f.replacement, { chipAsciiWhitespace: true }) : '',
        context: contextFor(text, f, toIndex),
        ucdVersion: UCD_VERSION,
      });
    }

    // PDF rows carry a page and no line/byte offset. Those columns are BLANK rather than zero —
    // a content stream has no meaningful line number, and inventing one would make the column a lie
    // in the artefact most likely to be read by a machine.
    for (const r of file.pdfRuns ?? []) {
      rows.push({
        file: `${file.name} (page ${r.page})`,
        line: '',
        column: '',
        byteOffset: '',
        byteLength: '',
        codePoint: '',
        unicodeName: '',
        block: '',
        script: '',
        category: 'Invisible text in a PDF',
        consequence: TIER_SLUGS[2],
        confidence: r.ocrLayer ? 'expected' : 'suspicious',
        reason: r.ocrLayer
          ? `Drawn invisibly (${r.reasons.join(', ')}) on a page that also paints an image — the signature of a scanned page with an OCR text layer, which is normal.`
          : `Drawn but not visible: ${r.reasons.join(', ')}.`,
        decodedPayload: r.recoverable
          ? visibleText(r.text, { chipAsciiWhitespace: true })
          : `[not recoverable: ${r.unrecoverableReason}]`,
        action: 'keep',
        replacement: '',
        context: '',
        ucdVersion: UCD_VERSION,
      });
    }

    for (const h of file.htmlFindings ?? []) {
      rows.push({
        file: file.name,
        line: '',
        column: '',
        byteOffset: '',
        byteLength: '',
        codePoint: '',
        unicodeName: '',
        block: '',
        script: '',
        category: `Text hidden by CSS (${h.where})`,
        consequence: TIER_SLUGS[2],
        confidence: h.routine ? 'notable' : 'suspicious',
        reason: `${mechanismLabel(h.mechanism)} — ${h.how}`,
        decodedPayload: visibleText(h.text, { chipAsciiWhitespace: true }),
        action: 'keep',
        replacement: '',
        context: '',
        ucdVersion: UCD_VERSION,
      });
    }
  }
  return rows;
}

/**
 * The plain-text note that ships beside `findings.csv`.
 *
 * The honesty gate lives in the interface and in `report.html`; without this it would be absent
 * from the artefact most likely to be opened by someone who never saw either, in the format most
 * likely to be sorted and forwarded.
 */
export const README_TXT = `charsight — how to read these files
====================================

findings.csv       one row per occurrence. Greppable and diffable: control characters are
                   escaped visibly, so one finding is always exactly one physical line, and
                   every hidden code point in the decoded_payload and context columns is
                   written as a visible token rather than as itself.
                   The "consequence" column is a word, not a number, on purpose — see below.
charsight-report.html   a self-contained page quoting every hidden message in full. It loads
                   nothing from the network and contains no scripts.
cleaned/*          your files, byte-identical to the originals except the spans you approved.
charsight-policy.json   exactly what was applied, so a run can be reproduced and reviewed.

WHAT THIS CANNOT TELL YOU
-------------------------
charsight reports what is in the bytes. It does not, and cannot, tell you who or what wrote
this text. Non-breaking spaces, narrow no-break spaces, curly quotes, em dashes and soft
hyphens come from word processors, keyboard layouts, publishing systems, content management
systems, and ordinary typing in languages other than English. A count of them is not evidence
about a person, and no part of this tool produces one.

The "consequence" column is deliberately not a number. It says what a character DOES —
carries-a-message, changes-what-you-see, breaks-your-tools, looks-like-something-else,
formatting — so that it cannot be sorted into a ranking in which a French narrow space sits
two rows below a smuggled instruction on one shared scale of suspicion.

A clean result means nothing matched the rules in this build. It is not a statement that a
file is safe: a file can carry perfectly visible instructions that are every bit as dangerous
as hidden ones.

Produced entirely in a browser. Nothing was uploaded.
charsight.benrichardson.dev
`;

export interface RunArtefacts {
  zip: Uint8Array;
  csv: string;
  report: string;
  policy: string;
  cleaned: { name: string; bytes: Uint8Array }[];
}

export function buildArtefacts(
  run: RunResult,
  texts: Map<string, string>,
  cleaned: { name: string; bytes: Uint8Array }[],
  policyJson: string,
  profileName: string,
): RunArtefacts {
  const csv = findingsCsv(findingRows(run, texts));
  const report = buildReport(run, profileName);

  const enc = new TextEncoder();
  const files: Record<string, Uint8Array> = {
    'findings.csv': enc.encode(csv),
    'charsight-report.html': enc.encode(report),
    'charsight-policy.json': enc.encode(policyJson),
    'README.txt': enc.encode(README_TXT),
  };
  for (const c of cleaned) files[`cleaned/${safeEntryName(c.name)}`] = c.bytes;

  return { zip: zipSync(files, { level: 6 }), csv, report, policy: policyJson, cleaned };
}

/**
 * A dropped folder's paths come from the file system and are not to be trusted verbatim.
 *
 * Separators, control bytes, leading dots and every run of dots are collapsed so a hostile archive
 * name cannot escape `cleaned/` in whatever the user extracts with.
 *
 * The control-byte class is built from escapes rather than written as a literal character class.
 * This is a tool that reports literal control bytes in other people's source; it does not put them
 * in its own.
 */
const CONTROL_BYTES = new RegExp('[\\u0000-\\u001f\\u007f]', 'g');
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i;

export function safeEntryName(name: string): string {
  const cleaned =
    name
      .replace(CONTROL_BYTES, '')
      .replace(/[\\/]+/g, '_')
      .replace(/\.{2,}/g, '.')
      .replace(/^\.+/, '')
      .replace(/[.\s]+$/u, '')
      .slice(0, 180) || 'file';
  return WINDOWS_RESERVED.test(cleaned) ? `_${cleaned}` : cleaned;
}

export function summarise(files: readonly FileResult[]): {
  payloads: number;
  suspicious: number;
  notable: number;
  expected: number;
} {
  let payloads = 0;
  let suspicious = 0;
  let notable = 0;
  let expected = 0;
  for (const f of files) {
    for (const x of f.findings) {
      if (x.severity === 'payload') payloads++;
      else if (x.severity === 'suspicious') suspicious++;
      else if (x.severity === 'notable') notable++;
      else expected++;
    }
  }
  return { payloads, suspicious, notable, expected };
}
